"use client";

import { getUserType } from "@nimble/datamodel/enums/userTypes";
import { useIsMobile } from "@nimble/react-state-hooks/use-mobile";
import { useEffect } from "react";
import {
  IntercomProps,
  IntercomProvider,
  useIntercom,
} from "react-use-intercom";

export type IntercomTrackerUser = {
  id: string;
  name: string;
  email: string;
  date_joined: string;
  intercom_hmac: string;
  profile: {
    user_type: number;
    district: {
      id: number;
      name: string;
      parent_organization: string | null;
    } | null;
  } | null;
};

export function buildIntercomUser(
  user: IntercomTrackerUser,
  currentApplication: string,
) {
  const date_joined = Math.floor(new Date(user.date_joined).getTime() / 1000);
  const userTypeLabel = getUserType(user.profile?.user_type)?.label;

  let companyInfo = {};

  if (user.profile?.district) {
    companyInfo = {
      company: {
        companyId: user.profile.district.id.toString(),
        name: user.profile.district.name,
        customAttributes: {
          parent_organization: user.profile.district.parent_organization || "",
        },
      },
    };
  }

  return {
    userId: user.id.toString(),

    name: user.name,
    email: user.email,

    createdAt: date_joined,
    userHash: user.intercom_hmac,

    customAttributes: {
      hostname: window.location.hostname,
      user_type: userTypeLabel,
      current_application: currentApplication,
    },

    ...companyInfo,
  };
}

export function bootIntercom(
  userData: IntercomProps,
  isMobile: boolean,
  boot: (props: IntercomProps) => void,
) {
  boot({
    // Intercom launcher settings
    hideDefaultLauncher: isMobile,

    ...userData,
  });
}

export function IntercomAuthTracker({
  currentApplication,
  isLoading,
  user,
}: {
  currentApplication: string;
  isLoading: boolean;
  user: IntercomTrackerUser | null;
}) {
  const { boot, update } = useIntercom();
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!isLoading && user) {
      const intercomUserData = buildIntercomUser(user, currentApplication);

      bootIntercom(intercomUserData, isMobile, boot);

      update(intercomUserData);
    }
  }, [isLoading, user, boot, update, isMobile, currentApplication]);

  return null;
}

export function IntercomTracker({
  appId,
  currentApplication,
  isLoading,
  user,
  children,
}: {
  appId: string;
  currentApplication: string;
  isLoading: boolean;
  user: IntercomTrackerUser | null;
  children: React.ReactNode;
}) {
  const isIntercomActive = !!appId;

  return (
    <IntercomProvider
      shouldInitialize={isIntercomActive}
      appId={appId}
      initializeDelay={1_000}
    >
      {children}
      {isIntercomActive ? (
        <IntercomAuthTracker
          currentApplication={currentApplication}
          isLoading={isLoading}
          user={user}
        />
      ) : null}
    </IntercomProvider>
  );
}
